import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import { useNotifications } from '../../context/useNotifications'
import type { NotificationRecord } from '../../types/schema'
import type { DigestInnerItem, NotificationRouterHandle } from '../../services/notificationRouter'
import { useGlobalActionLoading } from '../../context/useGlobalActionLoading'
import { useToast } from '../../context/useToast'
import { ConfirmActionModal } from '../Common/ConfirmActionModal'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { NotificationUndoSnackbar, type UndoState } from '../Common/NotificationUndoSnackbar'
import { CATEGORY_LABELS } from '../../utils/notificationCategories'
import { useNls } from '../../nls/nlsProvider'
import {
  getNotificationGroupLabel,
  getNotificationRelativeSubtitle,
  type ExperienceNlsT,
} from '../ExperienceNls'

type GroupLabel = 'Older' | 'Yesterday' | 'Today'
const GROUP_ORDER: GroupLabel[] = ['Older', 'Yesterday', 'Today']

const timestamp = (value: unknown) => {
  if (!value || typeof value !== 'object' || !('toMillis' in value)) return 0
  try {
    return (value as { toMillis: () => number }).toMillis()
  } catch {
    return 0
  }
}

const groupFor = (value: unknown): GroupLabel => {
  const millis = timestamp(value)
  if (!millis) return 'Older'
  const date = new Date(millis)
  const today = new Date()
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
  const startYesterday = startToday - 24 * 60 * 60 * 1000
  if (date.getTime() >= startToday) return 'Today'
  if (date.getTime() >= startYesterday) return 'Yesterday'
  return 'Older'
}

const timeLabel = (value: unknown) => {
  const millis = timestamp(value)
  if (!millis) return ''
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(millis)
}

const relativeSubtitle = (label: GroupLabel, notifications: NotificationRecord[], t: ExperienceNlsT) => {
  if (label === 'Today' || label === 'Yesterday') return getNotificationRelativeSubtitle(label, null, t)
  const newest = notifications.reduce((max, n) => Math.max(max, timestamp(n.createdAt)), 0)
  if (!newest) return ''
  const startToday = new Date()
  startToday.setHours(0, 0, 0, 0)
  const days = Math.max(2, Math.floor((startToday.getTime() - newest) / (24 * 60 * 60 * 1000)) + 1)
  return getNotificationRelativeSubtitle(label, days, t)
}

const NotificationCard = ({
  notification,
  onOpen,
  onDismiss,
  dismissLabel,
}: {
  notification: NotificationRecord
  onOpen: (notification: NotificationRecord) => void
  onDismiss: (notification: NotificationRecord) => void
  dismissLabel: string
}) => {
  const { t } = useNls()
  const unread = !notification.readAt
  const workspaceLabel = notification.workspaceId
    ? (notification.workspaceName || notification.workspaceId)
    : t('NLSNotificationRowAccount')

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 18 }}
      className={`notifCard${unread ? ' notifCard--unread' : ''}`}
    >
      <Pressable className="notifCard__hit" onClick={() => onOpen(notification)}>
        <span className="notifCard__meta">
          <span className="notifCard__category">
            {unread && <span className="notifCard__dot" aria-hidden="true" />}
            {CATEGORY_LABELS[notification.category ?? 'UPDATES']}
          </span>
          <span className="notifCard__workspace">{workspaceLabel}</span>
          <time className="notifCard__time">{timeLabel(notification.createdAt)}</time>
        </span>
        <strong className="notifCard__title">{notification.title}</strong>
        {notification.body && <p className="notifCard__body">{notification.body}</p>}
      </Pressable>
      <IconButton
        variant="tertiary"
        size="sm"
        className="notifCard__dismiss"
        label={dismissLabel}
        title={dismissLabel}
        icon={<CloseRoundedIcon fontSize="inherit" />}
        onClick={(event) => {
          event.stopPropagation()
          onDismiss(notification)
        }}
      />
    </motion.article>
  )
}

export const NotificationHub = ({
  onClose,
  notificationRouter,
}: {
  onClose: () => void
  notificationRouter?: NotificationRouterHandle
}) => {
  const { t } = useNls()
  const { notifications, unreadCount, markRead, clearNotification, clearAll, removeDigestItem, restoreNotifications } = useNotifications()
  const { runWithGlobalLoading } = useGlobalActionLoading()
  const { showToast } = useToast()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [overflowing, setOverflowing] = useState(false)
  const [undo, setUndo] = useState<UndoState | null>(null)
  const [confirmClearAllOpen, setConfirmClearAllOpen] = useState(false)
  const [confirmClearAllBusy, setConfirmClearAllBusy] = useState(false)
  const [expandedDigestId, setExpandedDigestId] = useState<string | null>(null)
  const currentDigest = useMemo(
    () => notifications.find((notification) => notification.id === expandedDigestId),
    [expandedDigestId, notifications],
  )

  useEffect(() => {
    if (!expandedDigestId || currentDigest) return
    const timer = window.setTimeout(() => setExpandedDigestId(null), 0)
    return () => window.clearTimeout(timer)
  }, [currentDigest, expandedDigestId])

  const closeHub = () => {
    setUndo(null)
    onClose()
  }

  const groups = useMemo(() => (
    GROUP_ORDER.map((label) => ({
      label,
      notifications: notifications.filter((n) => groupFor(n.createdAt) === label),
    })).filter((g) => g.notifications.length > 0)
  ), [notifications])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    setOverflowing(el.scrollHeight > el.clientHeight + 4)
  }, [groups])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 6
      setOverflowing(!atBottom && el.scrollHeight > el.clientHeight + 4)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  const activate = async (notification: NotificationRecord) => {
    if (notification.eventType === 'DIGEST' && (notification.digestCount ?? 0) > 1) {
      setExpandedDigestId(notification.id)
      return
    }
    await runWithGlobalLoading(async (signal) => {
      const result = notificationRouter?.resolveNotification(notification) ?? { ok: false, reason: 'unknown-event' as const }
      if (result.ok) {
        result.action()
        closeHub()
        if (signal.aborted) return
        if (!notification.readAt) await markRead(notification.id).catch(() => undefined)
        await clearNotification(notification.id).catch(() => undefined)
        return
      }

      const message = notification.body ? `${notification.title}\n${notification.body}` : notification.title
      showToast(message, result.reason === 'digest-expand' ? 'info' : 'warning')
      if (!notification.readAt) await markRead(notification.id).catch(() => undefined)
      await clearNotification(notification.id).catch(() => undefined)
      setUndo({
        ids: [notification.id],
        message: `${notification.title} — cleared (couldn't open)`,
      })
    }, {
      label: 'Opening notification',
      messages: [
        'Opening the workspace screen...',
        'Clearing the notification queue...',
        'Keeping repeated taps blocked...',
      ],
    })
  }

  const clearGroup = (group: { notifications: NotificationRecord[] }) => {
    group.notifications.forEach((n) => { void clearNotification(n.id) })
  }

  const dismiss = async (notification: NotificationRecord) => {
    await clearNotification(notification.id)
    setUndo({ ids: [notification.id], message: t('NLSExperienceNotificationDismissed') })
  }

  const clearAllWithUndo = async () => {
    await clearAll()
    setUndo({
      ids: notifications.map((notification) => notification.id),
      message: t('NLSExperienceNotificationAllCleared', { count: notifications.length }),
    })
  }

  const confirmClearAll = async () => {
    setConfirmClearAllBusy(true)
    try {
      await clearAll()
      setConfirmClearAllOpen(false)
    } finally {
      setConfirmClearAllBusy(false)
    }
  }

  const restoreUndo = async () => {
    const currentUndo = undo
    setUndo(null)
    if (currentUndo) await restoreNotifications(currentUndo.ids).catch(() => undefined)
  }

  const activateDigestItem = async (digestId: string, item: DigestInnerItem, index: number) => {
    const result = notificationRouter?.resolveDigestItem(item) ?? { ok: false, reason: 'unknown-event' as const }
    if (result.ok) {
      result.action()
      closeHub()
      await removeDigestItem(digestId, index).catch(() => undefined)
      return
    }
    showToast(item.body ? `${item.title}\n${item.body}` : item.title, result.reason === 'stale-deeplink' ? 'warning' : 'info')
    await removeDigestItem(digestId, index).catch(() => undefined)
    setUndo({ ids: [digestId], message: `${item.title} — cleared (couldn't open)` })
  }

  const newestGroupIndex = groups.length - 1

  return (
    <section className="notifHub" aria-label={t('NLSExperienceNotificationHubAria')}>
      <header className="notifHub__header">
        <span aria-hidden="true" />
        <div className="notifHub__headerTitle">
          <span className="notifHub__eyebrow">{t('NLSExperienceNotificationSignalStream')}</span>
          <h2>{t('NLSExperienceNotificationsTitle')}</h2>
        </div>
        <span className="notifHub__count" aria-label={t('NLSExperienceNotificationUnreadAria', { count: unreadCount })}>{unreadCount}</span>
      </header>

      <div className="notifHub__scroll" ref={scrollRef}>
        {currentDigest ? (
          <section className="notifDigestExpansion" aria-label={currentDigest.title}>
            <header className="notifDigestExpansion__header">
              <Button
                variant="tertiary"
                size="sm"
                icon={<ArrowBackRoundedIcon sx={{ fontSize: 18 }} />}
                onClick={() => setExpandedDigestId(null)}
                aria-label={t('NLSCommonActionBack')}
              >
                {t('NLSCommonActionBack')}
              </Button>
              <strong>{currentDigest.title}</strong>
            </header>
            <div className="notifGroup__items">
              <AnimatePresence initial={false}>
                {currentDigest.digestItems?.map((item, index) => {
                  const innerNotification: NotificationRecord = {
                    ...currentDigest,
                    id: `${currentDigest.id}-${index}`,
                    title: item.title,
                    body: item.body,
                    category: item.category ?? currentDigest.category,
                  }
                  return (
                    <NotificationCard
                      key={`${currentDigest.id}-${index}`}
                      notification={innerNotification}
                      onOpen={() => { void activateDigestItem(currentDigest.id, item, index) }}
                      onDismiss={() => { void removeDigestItem(currentDigest.id, index) }}
                      dismissLabel={t('NLSExperienceNotificationDismissAria')}
                    />
                  )
                })}
              </AnimatePresence>
            </div>
          </section>
        ) : groups.length === 0 ? (
          <div className="notifHub__empty">
            <NotificationsNoneRoundedIcon sx={{ fontSize: 34 }} />
            <strong>{t('NLSExperienceNotificationNoSignals')}</strong>
            <span>{t('NLSExperienceNotificationEmptyBody')}</span>
          </div>
        ) : groups.map((group, idx) => (
          <section key={group.label} className="notifGroup">
            <div className="notifGroup__header">
              <div className="notifGroup__label">
                <span className="notifGroup__name">{getNotificationGroupLabel(group.label, t)}</span>
                <span className="notifGroup__rel">{relativeSubtitle(group.label, group.notifications, t)}</span>
              </div>
              <IconButton
                variant="tertiary"
                size="sm"
                label={t('NLSExperienceNotificationClearGroupAria', { group: getNotificationGroupLabel(group.label, t) })}
                title={t('NLSExperienceNotificationClearGroupTitle', { group: getNotificationGroupLabel(group.label, t) })}
                icon={<DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />}
                onClick={() => clearGroup(group)}
              />
            </div>
            {idx === newestGroupIndex && overflowing && (
              <div className="notifGroup__scrollHint" aria-hidden="true">
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14 }} />
                <span>{t('NLSExperienceNotificationScrollLatest')}</span>
              </div>
            )}
            <div className="notifGroup__items">
              <AnimatePresence initial={false}>
                {group.notifications.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    notification={notification}
                    onOpen={(entry) => { void activate(entry) }}
                    onDismiss={(entry) => { void dismiss(entry) }}
                    dismissLabel={t('NLSExperienceNotificationDismissAria')}
                  />
                ))}
              </AnimatePresence>
            </div>
          </section>
        ))}
      </div>

      <footer className="notifHub__footer">
        <IconButton
          variant="tertiary"
          size="sm"
          label={t('NLSCommonActionBack')}
          title={t('NLSCommonActionBack')}
          icon={<ArrowBackRoundedIcon sx={{ fontSize: 20 }} />}
          onClick={closeHub}
        />
        {notifications.length > 0 ? (
          <Button
            variant="tertiary"
            size="sm"
            icon={<DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={() => notifications.length > 5 ? setConfirmClearAllOpen(true) : void clearAllWithUndo()}
            aria-label={t('NLSExperienceNotificationClearAllAria')}
            title={t('NLSExperienceNotificationClearAllTitle')}
          >
            {t('NLSExperienceNotificationClearAll')}
          </Button>
        ) : <span aria-hidden="true" />}
      </footer>
      <NotificationUndoSnackbar undo={undo} onUndo={() => void restoreUndo()} onDismiss={() => setUndo(null)} />
      <ConfirmActionModal
        open={confirmClearAllOpen}
        onClose={() => setConfirmClearAllOpen(false)}
        onConfirm={() => void confirmClearAll()}
        title={t('NLSExperienceNotificationClearAllConfirmTitle')}
        body={t('NLSExperienceNotificationClearAllConfirmBody')}
        confirmLabel={t('NLSExperienceNotificationClearAll')}
        danger
        busy={confirmClearAllBusy}
      />
    </section>
  )
}
